import * as THREE from 'three';
import { clamp } from '../shared/utils';

export class CFRenderer {
  public renderer: THREE.WebGLRenderer;
  public scene: THREE.Scene;
  public camera: THREE.PerspectiveCamera;
  public pixelRatio: number;
  public renderScale: number;
  public fps = 0;
  public frameTime = 0;

  private frameCount = 0;
  private fpsTimer = 0;
  private fpsDisplay = 0;

  constructor(canvas: HTMLCanvasElement) {
    this.pixelRatio = Math.min(window.devicePixelRatio, 2);
    this.renderScale = 1;

    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: 'high-performance',
      alpha: false,
    });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(this.pixelRatio);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.1;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.autoClear = true;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x0a0b0e);
    this.scene.fog = new THREE.FogExp2(0x0a0b0e, 0.012);

    this.camera = new THREE.PerspectiveCamera(
      76,
      window.innerWidth / window.innerHeight,
      0.04,
      300
    );
    this.camera.rotation.order = 'YXZ';

    window.addEventListener('resize', this.onResize.bind(this));
  }

  private onResize(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w * this.renderScale, h * this.renderScale);
    this.renderer.domElement.style.width = `${w}px`;
    this.renderer.domElement.style.height = `${h}px`;
  }

  updateFps(dt: number): void {
    this.frameTime = dt;
    this.frameCount++;
    this.fpsTimer += dt;
    if (this.fpsTimer >= 1.0) {
      this.fpsDisplay = this.frameCount;
      this.frameCount = 0;
      this.fpsTimer -= 1.0;
    }
    this.fps = this.fpsDisplay;
  }

  setRenderScale(scale: number): void {
    this.renderScale = clamp(scale, 0.5, 1.5);
    this.onResize();
  }

  setFOV(fov: number): void {
    this.camera.fov = clamp(fov, 60, 120);
    this.camera.updateProjectionMatrix();
  }

  render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  clear(): void {
    this.scene.clear();
  }

  dispose(): void {
    this.renderer.dispose();
    window.removeEventListener('resize', this.onResize.bind(this));
  }
}

export class LightingSystem {
  public ambient: THREE.HemisphereLight;
  public sun: THREE.DirectionalLight;
  public fillLight: THREE.DirectionalLight;
  private scene: THREE.Scene;

  constructor(scene: THREE.Scene) {
    this.scene = scene;

    this.ambient = new THREE.HemisphereLight(0xc8d0e0, 0x1a1d26, 2.0);
    this.ambient.position.set(0, 50, 0);
    scene.add(this.ambient);

    this.sun = new THREE.DirectionalLight(0xfff4e0, 2.4);
    this.sun.position.set(30, 50, 20);
    this.sun.castShadow = true;
    this.sun.shadow.camera.left = -60;
    this.sun.shadow.camera.right = 60;
    this.sun.shadow.camera.top = 60;
    this.sun.shadow.camera.bottom = -60;
    this.sun.shadow.camera.near = 0.5;
    this.sun.shadow.camera.far = 150;
    this.sun.shadow.mapSize.set(2048, 2048);
    this.sun.shadow.bias = -0.0005;
    this.sun.shadow.normalBias = 0.02;
    scene.add(this.sun);

    this.fillLight = new THREE.DirectionalLight(0x4060a0, 0.4);
    this.fillLight.position.set(-20, 10, -30);
    scene.add(this.fillLight);
  }

  setSunColor(color: number): void {
    this.sun.color.set(color);
  }

  setSunIntensity(intensity: number): void {
    this.sun.intensity = intensity;
  }

  setSunPosition(x: number, y: number, z: number): void {
    this.sun.position.set(x, y, z);
  }

  setAmbientColor(color: number): void {
    this.ambient.color.set(color);
  }

  setAmbientIntensity(intensity: number): void {
    this.ambient.intensity = intensity;
  }

  setFog(color: number, near: number, far: number): void {
    this.scene.fog = new THREE.Fog(color, near, far);
    this.scene.background = new THREE.Color(color);
  }

  dispose(): void {
    this.scene.remove(this.ambient);
    this.scene.remove(this.sun);
    this.scene.remove(this.fillLight);
  }
}
